import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle, Terminal, Loader2, Building2 } from 'lucide-react'

interface Organization {
  id: string
  name: string
}

export function DevicePage() {
  const { user, signInWithGoogle } = useAuth()
  const [searchParams] = useSearchParams()
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'select-org'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)

  // Pre-fill code from URL query param
  useEffect(() => {
    const codeParam = searchParams.get('code')
    if (codeParam) {
      setCode(codeParam.toUpperCase())
    }
  }, [searchParams])

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Format code as XXXX-XXXX-XXXX-XXXX
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (value.length > 4) {
      value = value.slice(0, 4) + '-' + value.slice(4)
    }
    if (value.length > 9) {
      value = value.slice(0, 9) + '-' + value.slice(9)
    }
    if (value.length > 14) {
      value = value.slice(0, 14) + '-' + value.slice(14, 18)
    }
    setCode(value)
  }

  const handleAuthorize = async (organizationId?: string) => {
    if (!user) return

    setStatus('loading')
    setErrorMessage('')

    try {
      const idToken = await user.getIdToken()

      const response = await fetch('/api/admin/device/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          userCode: code.replace(/-/g, ''),
          organizationId,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        // Check if we need to select an organization
        if (result.organizations && result.organizations.length > 0) {
          setOrganizations(result.organizations)
          setStatus('select-org')
          return
        }
        setStatus('error')
        setErrorMessage(result.error || 'Failed to authorize device')
        return
      }

      setStatus('success')
    } catch {
      setStatus('error')
      setErrorMessage('Failed to connect to server')
    }
  }

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org)
  }

  const handleConfirmOrg = () => {
    if (selectedOrg) {
      handleAuthorize(selectedOrg.id)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle()
    } catch (error) {
      console.error('Error logging in:', error)
    }
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Device Authorized
            </CardTitle>
            <CardDescription className="text-center">
              You can now close this window and return to your CLI.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <Terminal className="h-12 w-12 text-gray-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Authorize CLI
            </CardTitle>
            <CardDescription className="text-center">
              Sign in to authorize the Logg CLI on your device
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleGoogleLogin}
              className="w-full"
              size="lg"
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Select organization
  if (status === 'select-org') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <Building2 className="h-12 w-12 text-gray-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Select Organization
            </CardTitle>
            <CardDescription className="text-center">
              Which organization should this CLI be authorized for?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleSelectOrg(org)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                    selectedOrg?.id === org.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-medium">{org.name}</div>
                  <div className="text-sm text-gray-500">{org.id}</div>
                </button>
              ))}
            </div>

            <Button
              onClick={handleConfirmOrg}
              className="w-full"
              size="lg"
              disabled={!selectedOrg}
            >
              Authorize for {selectedOrg?.name || 'Selected Organization'}
            </Button>

            <p className="text-sm text-gray-500 text-center">
              Signed in as {user.email}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Logged in - show code entry
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Terminal className="h-12 w-12 text-gray-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Authorize CLI
          </CardTitle>
          <CardDescription className="text-center">
            Confirm the code below matches your terminal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'error' && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="code">Device Code</Label>
            <Input
              id="code"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              value={code}
              onChange={handleCodeChange}
              maxLength={19}
              className="text-center text-xl tracking-wider font-mono"
              autoFocus
            />
          </div>

          <Button
            onClick={() => handleAuthorize()}
            className="w-full"
            size="lg"
            disabled={code.length !== 19 || status === 'loading'}
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Authorizing...
              </>
            ) : (
              'Authorize Device'
            )}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            Signed in as {user.email}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
